/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import MonsterOpen5e from 'model/formats/MonsterOpen5e';
import CreatureOpen5e from 'model/formats/CreatureOpen5e';
import Monster from 'model/monster/Monster';
import { sortSourceGroups } from 'model/formats/Open5eDocument';

export interface MonsterOption {
  value: string;
  label: string;
}

export interface MonsterOptionGroup {
  label: string;
  options: MonsterOption[];
}

const byLabel = (m1: MonsterOption, m2: MonsterOption) => m1.label.localeCompare(m2.label);

// Builds the grouped option list for the monster dropdown: 'My Monsters' first, then 'GGG Community'
// (other users' public monsters), then one group per catalog source. The leading two groups are
// always kept ahead of the source groups; only the source groups get sorted/pruned.
export function buildMonsterSelectorOptions(
    monsters: MonsterOpen5e[],
    creatures: CreatureOpen5e[],
    sources: Map<string, { name: string; licenses: string[] }>,
    myMonsters: Monster[],
    communityMonsters: { username: string; monster: Monster }[]): MonsterOptionGroup[] {

  const monsBySource = monsters.reduce((acc: Map<string, MonsterOption[]>, monster: MonsterOpen5e) => {
    const key = monster.document__slug;
    if (!acc.has(key)) {
      acc.set(key, []);
    }
    acc.get(key)!.push({ value: monster.slug, label: monster.name });
    return acc;
  }, new Map<string, MonsterOption[]>());

  const creatsBySource = creatures.reduce((acc: Map<string, MonsterOption[]>, creature: CreatureOpen5e) => {
    const key = creature.document.key;
    if (!acc.has(key)) {
      acc.set(key, []);
    }
    acc.get(key)!.push({ value: creature.key, label: creature.name });
    return acc;
  }, new Map<string, MonsterOption[]>());

  // '5e Core Rules' is pinned to the top of the source groups; the rest follow alphabetically.
  const sourceGroups: MonsterOptionGroup[] = sortSourceGroups(
    Array.from(sources.entries(), ([key, license]) => ({
      label: license.name,
      options: [...(monsBySource.get(key) || []), ...(creatsBySource.get(key) || [])].sort(byLabel),
    })));

  // Leading groups appear before the source groups, in order: My Monsters, then the GGG Community.
  const leadingGroups: MonsterOptionGroup[] = [];
  if (myMonsters && myMonsters.length > 0) {
    leadingGroups.push({
      label: 'My Monsters',
      options: myMonsters.map(mon => ({ value: 'user_' + mon.name, label: mon.name })).sort(byLabel),
    });
  }
  if (communityMonsters && communityMonsters.length > 0) {
    // The value embeds the owner's id so two same-named monsters from different owners stay
    // distinct (Firebase UIDs are alphanumeric with no '_', so the name is recoverable by
    // splitting on the first '_' after the 'community_' prefix).
    leadingGroups.push({
      label: 'GGG Community',
      options: communityMonsters.map(({ username, monster }) =>
        ({ value: 'community_' + monster.ownerId + '_' + monster.name, label: `${monster.name} (${username})` }))
        .sort(byLabel),
    });
  }
  return [...leadingGroups, ...sourceGroups];
}
